/**
 * ImageManager - Gerenciador de imagens e vídeos com upload
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Upload de arquivos do dispositivo
 * - Adição de imagens via URL
 * - Preview de imagens e vídeos
 * - Remoção individual e em massa
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - Button, Input, Label (shadcn/ui)
 */

import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useTranslation } from "../translations";

const ImageManager = ({ images = [], onImagesChange }) => {
  const { t } = useTranslation();
  const [imageUrls, setImageUrls] = useState(images);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState([]);

  // Sync with parent when images prop changes
  useEffect(() => {
    setImageUrls(images);
  }, [images]);

  const isValidUrl = (url) => {
    if (url.startsWith('data:')) return true; // Base64 images
    if (url.startsWith('http://') || url.startsWith('https://')) return true;
    return false;
  };

  const addImage = () => {
    const url = newImageUrl.trim();
    if (!url) {
      alert('Por favor, insira uma URL válida');
      return;
    }

    if (!isValidUrl(url)) {
      alert('URL inválida. Verifique se começou com http:// ou https://');
      return;
    }

    try {
      const updatedImages = [...imageUrls, url];
      setImageUrls(updatedImages);
      onImagesChange(updatedImages);
      setNewImageUrl('');
      console.log('Image added successfully:', url);
    } catch (error) {
      console.error('Error adding image:', error);
      alert('Erro ao adicionar imagem: ' + error.message);
    }
  };

  const removeImage = (index) => {
    const updatedImages = imageUrls.filter((_, i) => i !== index);
    setImageUrls(updatedImages);
    onImagesChange(updatedImages);
  };

  const clearAllImages = () => {
    setImageUrls([]);
    onImagesChange([]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addImage();
    }
  };

  // Handle file upload from device
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(files.map(f => f.name));

    files.forEach((file) => {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/mov', 'video/avi', 'video/webm'];
      if (!validTypes.includes(file.type)) {
        alert(`Arquivo ${file.name} não é suportado. Use: JPG, PNG, GIF, WebP, MP4, MOV, AVI, WebM`);
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`Arquivo ${file.name} é muito grande. Máximo 10MB.`);
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Url = event.target.result;
        const updatedImages = [...imageUrls, base64Url];
        setImageUrls(updatedImages);
        onImagesChange(updatedImages);
        
        // Remove from uploading list
        setUploadingFiles(prev => prev.filter(name => name !== file.name));
      };
      reader.onerror = () => {
        alert(`Erro ao carregar ${file.name}`);
        setUploadingFiles(prev => prev.filter(name => name !== file.name));
      };
      reader.readAsDataURL(file);
    });

    // Clear file input
    e.target.value = '';
  };

  return (
    <div className="space-y-4">
      {/* Upload from Device */}
      <div className="border border-green-300 rounded-lg p-4 bg-green-50">
        <Label className="text-sm font-medium text-green-800">📱 Upload do Celular/Computador</Label>
        <div className="mt-2">
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
          />
        </div>
        <p className="text-xs text-green-600 mt-1">
          📸 Selecione fotos e vídeos diretamente do seu dispositivo (máx. 10MB cada)
        </p>
        {uploadingFiles.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-blue-600">📤 {t('uploading')}: {uploadingFiles.join(', ')}</p>
          </div>
        )}
      </div>

      {/* Add URL */}
      <div className="border border-gray-300 rounded-lg p-4">
        <Label className="text-sm font-medium">🔗 {t('addByUrl')}</Label>
        <div className="flex space-x-2 mt-2">
          <Input
            value={newImageUrl}
            onChange={(e) => setNewImageUrl(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="https://exemplo.com/imagem.jpg"
            className="flex-1"
          />
          <Button 
            type="button" 
            onClick={addImage}
            disabled={!newImageUrl.trim()}
          >
            {t('add')}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          💡 Use URLs completas: Google Fotos, Imgur, ou qualquer link de imagem/vídeo
        </p>
      </div>

      {/* Current Images */}
      {imageUrls.length > 0 && (
        <div className="border border-gray-300 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <Label className="text-sm font-medium">
              Imagens/Vídeos Atuais ({imageUrls.length})
            </Label>
            <Button 
              type="button" 
              variant="destructive" 
              size="sm"
              onClick={clearAllImages}
            >
              🗑️ Apagar Todas
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {imageUrls.map((url, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                  {url.includes('youtube.com') || url.includes('youtu.be') || url.includes('.mp4') || url.includes('.mov') || url.startsWith('data:video/') ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                      <div className="text-white text-center">
                        <div className="text-2xl mb-1">📹</div>
                        <div className="text-xs">Vídeo</div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt={`Imagem ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkVycm8gYW8gY2FycmVnYXI8L3RleHQ+PC9zdmc+';
                      }}
                    />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                >
                  ×
                </button>
                <p className="text-xs text-gray-500 mt-1 truncate" title={url}>
                  {url.startsWith('data:') ? `Arquivo carregado ${index + 1}` : (url.length > 40 ? url.substring(0, 40) + '...' : url)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {imageUrls.length === 0 && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <div className="text-4xl mb-2">📷</div>
          <p className="text-gray-500">Nenhuma imagem adicionada ainda</p>
          <p className="text-xs text-gray-400">📱 Use o upload acima ou cole URLs de imagens</p>
        </div>
      )}
    </div>
  );
};

export default ImageManager;
